import {
  PdfGen,
  PdfUnit,
  PdfSize,
  PdfRect,
  PdfPoint,
  PdfBrush,
  PdfColor,
  ETextAlign,
} from '../../pdf/pdf';
import { ILayoutLabelGen } from '../layoutLabel';
import { OptsLabel } from '../optsLabel';
import { IItemCount } from '@educraft/base';
import { GUtil } from '../../pdf/gutil';
import { GTuple } from '../../pdf/glabel';
import { calcOrigin } from '../../core/genHelper';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';
import '../../pdf/draw/label';
import '../../pdf/draw/cut';

/**
 * Obchod (Shop) - Shopping math exercise (Label version)
 *
 * Landscape A4, 5×2 cards per page. Needs exactly 10 items.
 * opts.list[i].left  = product image label (A)
 * opts.list[i].right = product text/name label (B)
 *
 * Pages generated:
 * 1. Products without name labels
 * 2. Products with name labels
 * 3–10. Shopping lists (1–4 products) × (image/label) × (total/return)
 *
 * Migrated from C# EduFrame.Img10.Generators.Obchod
 */

const PRICE_ARRAY = [1, 2, 3, 5, 10, 15, 20, 25, 30, 50, 60, 80, 85, 90, 95];
const PAYMENT_ARRAY = [10, 20, 50, 100, 200, 500, 1000];
const ITEM_COUNT = 10;

const LIGHT_SKY_BLUE = new PdfBrush(PdfColor.fromRgb(135, 206, 250));

export class Obchod implements ILayoutLabelGen, IItemCount {
  countDefault(): number {
    return ITEM_COUNT;
  }
  countMin(): number {
    return ITEM_COUNT;
  }
  countMax(): number {
    return ITEM_COUNT;
  }

  public async generate(pdf: PdfGen, opts: OptsLabel): Promise<void> {
    const {random} = opts.data;
    const margin = PdfUnit.FromMillimeters(5);
    const items = opts.list.slice(0, ITEM_COUNT);

    // Pick 10 random prices from the price array
    const shuffledPrices = [...PRICE_ARRAY].sort(() => random.next(0, 2) - 1);
    const prices = shuffledPrices.slice(0, ITEM_COUNT);

    const {lang} = opts.data;
    const shopOrder = lang.Shop_Order ?? 'Order';
    const shopHowMuch = lang.Shop_HowMuchInTotal ?? 'How much will I pay in total?';
    const shopReturn = lang.Shop_Return ?? 'How much change will I get?';
    const shopPaying = lang.Shop_Paying ?? 'I pay {0}';

    genProducts(pdf, opts.data, items, prices, margin, false);
    genProducts(pdf, opts.data, items, prices, margin, true);

    for (let count = 1; count <= 4; count++) {
      for (const useImage of [false, true]) {
        for (const returnMode of [false, true]) {
          genList(
            pdf,
            opts.data,
            items,
            prices,
            margin,
            count,
            useImage,
            returnMode,
            shopOrder,
            shopHowMuch,
            shopReturn,
            shopPaying
          );
        }
      }
    }
  }
}

function genProducts(
  pdf: PdfGen,
  data: OptsLabel['data'],
  items: GTuple[],
  prices: number[],
  margin: number,
  withLabel: boolean
): void {
  const page = pdf.addPage(true); // landscape
  const { origin, size } = calcOrigin(5, 2, margin, PdfSize.A4H);

  const priceHeight = PdfUnit.FromMillimeters(20);
  const labelHeight = 27; // ~27pt, matching C# user units

  const labelTexts = items.map((i) => i.right.getText());
  const labelFont = page.adjustFont(size.width, labelTexts, GUtil.baseFont);

  const {lang} = data;
  const priceText = lang.Shop_Price ?? '{0},–';
  const priceFont = page.adjustFont(size.width, [priceText], GUtil.baseFont);

  for (let x = 0; x < 5; x++) {
    for (let y = 0; y < 2; y++) {
      const index = y * 5 + x;
      const rect = new PdfRect(
        origin.x + x * size.width,
        origin.y + y * size.height,
        size.width,
        size.height
      );
      const imageHeight = rect.height - priceHeight - (withLabel ? labelHeight : 0);
      const imageRect = new PdfRect(rect.x, rect.y, rect.width, imageHeight);
      const textRect = new PdfRect(
        rect.x,
        rect.y + rect.height - priceHeight,
        rect.width,
        priceHeight
      );

      page.drawLabel(items[index].left, { rect: imageRect });

      if (withLabel) {
        const labelRect = new PdfRect(imageRect.x, imageRect.bottom, rect.width, labelHeight);
        page.drawLabel(items[index].right, { rect: labelRect, font: labelFont });
      }

      // Price tag shape (arrow pointing left)
      const priceRect = new PdfRect(
        textRect.x + textRect.width / 2,
        textRect.y + 5,
        textRect.width / 2,
        textRect.height - 10
      );
      const pricePoints: PdfPoint[] = [
        new PdfPoint(priceRect.x + priceRect.height / 2, priceRect.bottom),
        new PdfPoint(priceRect.x, priceRect.y + priceRect.height / 2),
        new PdfPoint(priceRect.x + priceRect.height / 2, priceRect.y),
        new PdfPoint(priceRect.right, priceRect.y),
        new PdfPoint(priceRect.right, priceRect.bottom),
      ];
      page.drawPolygon(null, LIGHT_SKY_BLUE, pricePoints);
      page.drawStringMulti(
        priceText.replace('{0}', prices[index].toString()),
        priceFont,
        PdfBrush.Black,
        null,
        textRect,
        ETextAlign.CenterRight
      );

      page.drawRectangle(GUtil.cutPen, null, rect);
    }
  }
}

function genList(
  pdf: PdfGen,
  data: OptsLabel['data'],
  items: GTuple[],
  prices: number[],
  margin: number,
  count: number,
  useImage: boolean,
  returnMode: boolean,
  shopOrder: string,
  shopHowMuch: string,
  shopReturn: string,
  shopPaying: string
): void {
  const page = pdf.addPage(true); // landscape
  const { origin, size } = calcOrigin(5, 2, margin, PdfSize.A4H);
  const labelHeight = PdfUnit.FromMillimeters(30);
  const headerHeight = PdfUnit.FromMillimeters(10);
  const {random} = data;

  const allTexts = items.map((i) => (useImage ? i.left.getText() : i.right.getText()));
  const allRects: PdfRect[] = [];
  if (count === 4) {
    const sw = size.width / 2;
    const sh = (size.height - labelHeight - headerHeight) / 2;
    for (let x1 = 0; x1 < 2; x1++) {
      for (let y1 = 0; y1 < 2; y1++) {
        allRects.push(new PdfRect(0, 0, sw, sh));
      }
    }
  } else {
    const sh = (size.height - labelHeight - headerHeight) / count;
    for (let y1 = 0; y1 < count; y1++) {
      allRects.push(new PdfRect(0, 0, size.width, sh));
    }
  }
  const minCellSize = new PdfSize(
    Math.min(...allRects.map((r) => r.width)),
    Math.min(...allRects.map((r) => r.height))
  );
  const cellFont = page.adjustFontMulti(
    allTexts.map((t) => [t]),
    GUtil.baseFont,
    minCellSize
  );

  for (let x = 0; x < 5; x++) {
    for (let y = 0; y < 2; y++) {
      // Pick 'count' random product indices
      const allIndices = Array.from({ length: ITEM_COUNT }, (_, i) => i);
      for (let i = allIndices.length - 1; i > 0; i--) {
        const j = random.next(0, i + 1);
        [allIndices[i], allIndices[j]] = [allIndices[j], allIndices[i]];
      }
      const products = allIndices.slice(0, count);

      const rect = new PdfRect(
        origin.x + x * size.width,
        origin.y + y * size.height,
        size.width,
        size.height
      );
      const headerRect = new PdfRect(rect.x, rect.y, rect.width, headerHeight);
      const imageRect = new PdfRect(
        rect.x,
        rect.y + headerHeight,
        rect.width,
        rect.height - labelHeight - headerHeight
      );
      const textRect = new PdfRect(imageRect.x, imageRect.bottom, rect.width, labelHeight);

      page.drawStringMulti(
        shopOrder,
        GUtil.baseFont,
        PdfBrush.Black,
        null,
        headerRect,
        ETextAlign.Center
      );

      // Build sub-rects for products
      const rects: PdfRect[] = [];
      if (count === 4) {
        const sw = imageRect.width / 2;
        const sh = imageRect.height / 2;
        for (let x1 = 0; x1 < 2; x1++) {
          for (let y1 = 0; y1 < 2; y1++) {
            rects.push(new PdfRect(imageRect.x + x1 * sw, imageRect.y + y1 * sh, sw, sh));
          }
        }
      } else {
        const sh = imageRect.height / count;
        for (let y1 = 0; y1 < count; y1++) {
          rects.push(new PdfRect(imageRect.x, imageRect.y + y1 * sh, imageRect.width, sh));
        }
      }

      for (let i = 0; i < rects.length; i++) {
        const r = rects[i];
        const item = items[products[i]];
        page.drawLabel(useImage ? item.left : item.right, { rect: r, font: cellFont });
      }

      const t1Rect = new PdfRect(textRect.x, textRect.y, textRect.width, textRect.height / 2);
      const t2Rect = new PdfRect(
        textRect.x,
        textRect.y + textRect.height / 2,
        textRect.width,
        textRect.height / 2
      );

      page.drawStringMulti(
        shopHowMuch,
        GUtil.baseFont,
        PdfBrush.Black,
        null,
        t1Rect,
        ETextAlign.Center
      );

      if (returnMode) {
        const total = products.reduce((sum, i) => sum + prices[i], 0);
        const validPayments = PAYMENT_ARRAY.filter((p) => p >= total).slice(0, 2);
        const paying = validPayments[random.next(0, validPayments.length)];
        const returnText = shopReturn.replace('{0}', total.toString());
        const payingText = shopPaying.replace('{0}', paying.toString());
        page.drawStringsMulti(
          [returnText, payingText],
          GUtil.baseFont,
          PdfBrush.Black,
          null,
          t2Rect,
          ETextAlign.Center
        );
      }

      page.drawRectangle(GUtil.basePen, null, imageRect);
      page.drawRectangle(GUtil.cutPen, null, rect);
    }
  }
}
